import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit"
import { ProductFields } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import {
    addInlineAttachmentDataApi,
    getAttachmentCreatorDataApi,
    removeInlineAttachmentDataApi,
    updateAttachmentValueDataApi,
} from "../../../../apis/attachments"
import { addErrorNotification } from "../../../notifications/actions"
import { Fields } from "../../"
import {
    defaultUpdatingFieldErrorMessage,
    fetchFields,
    handleFieldUpdateFeedback,
} from "../../slice"

export const defaultImageErrorMessage = "Failed to load image "
export const defaultGettingCreatorErrorMessage =
    "Something went wrong retrieving the creator of the attachment "
export const defaultAddingAttachmentErrorMessage =
    "Something went wrong adding the attachment "
export const defaultRemovingAttachmentErrorMessage =
    "Something went wrong removing the attachment "
export const defaultGettingAttachmentErrorMessage =
    "Something went wrong retrieving the attachment "

interface AttachmentParameters {
    field: Fields.InlineAttachmentField
    files: File[]
}

interface UpdateAttachment {
    field: Fields.InlineAttachmentField
    newValue: Partial<Api.Attachment>
    attachment?: ProductFields.Attachment
    unknownCreator?: string
}

interface RemoveAttachment {
    field: Fields.InlineAttachmentField
    attachment?: ProductFields.Attachment
}

export const attachmentsReducersBuilder = (
    builder: ActionReducerMapBuilder<Fields.Item>,
): void => {
    builder
        .addCase(addAttachment.pending, (state: Fields.Item) => {
            state.isLoading = true
        })
        .addCase(addAttachment.fulfilled, (state: Fields.Item) => {
            state.isLoading = false
        })
        .addCase(addAttachment.rejected, (state: Fields.Item) => {
            state.isLoading = false
        })

    builder.addCase(addCreatorToAttachment.fulfilled, (state, action) => {
        if (!action.payload) {
            return
        }
        const { field, attachment } = action.payload
        if (!attachment) {
            return
        }
        const currentAttachments: ProductFields.Attachment[] = state.fields[
            field.fieldId
        ].value as ProductFields.Attachment[]

        if (!currentAttachments || currentAttachments.length === 0) {
            return
        }

        const index = currentAttachments.findIndex(
            (a: ProductFields.Attachment) => a.id === attachment.id,
        )
        currentAttachments[index] = attachment
        state.fields[field.fieldId].value = currentAttachments
    })

    builder
        .addCase(removeAttachment.pending, (state: Fields.Item) => {
            state.isLoading = true
        })
        .addCase(removeAttachment.fulfilled, (state: Fields.Item) => {
            state.isLoading = false
        })
        .addCase(removeAttachment.rejected, (state: Fields.Item) => {
            state.isLoading = false
        })

    builder
        .addCase(updateAttachment.pending, (state: Fields.Item) => {
            state.isLoading = true
        })
        .addCase(updateAttachment.fulfilled, (state: Fields.Item) => {
            state.isLoading = false
        })
        .addCase(updateAttachment.rejected, (state: Fields.Item) => {
            state.isLoading = false
        })
}

export async function getCreatorLabel(
    attachmentIds: number[],
): Promise<PromiseSettledResult<Partial<Api.AttachmentCreator>>[]> {
    const promises: Promise<Partial<Api.AttachmentCreator>>[] = []
    attachmentIds.map((attachmentId) => {
        promises.push(
            getAttachmentCreatorDataApi(attachmentId, {
                $select: "Label",
            }),
        )
    })
    return await Promise.allSettled(promises)
}

export const addCreatorToAttachment = createAsyncThunk(
    "fields/inlineAttachment/addCreatorToAttachment",
    async (
        {
            field,
            attachment,
            unknownCreator,
        }: {
            field: Fields.InlineAttachmentField
            attachment: ProductFields.Attachment
            unknownCreator?: string
        },
        { dispatch },
    ) => {
        if (!attachment) {
            return
        }
        let creator: string | null | undefined = ""
        try {
            creator = (
                await getAttachmentCreatorDataApi(attachment.id, {
                    $select: "Label",
                })
            ).Label
        } catch (error) {
            const errorMessage = `${defaultGettingCreatorErrorMessage} file: ${attachment.fileName}`
            console.error(`${errorMessage}: `, error)
            dispatch(addErrorNotification(errorMessage))
        }
        const updatedAttachment = {
            ...attachment,
            creator: creator || unknownCreator,
        }
        return { field, attachment: updatedAttachment }
    },
)

export const updateAttachment = createAsyncThunk(
    "fields/inlineAttachment/updateAttachment",
    async (
        { field, newValue, attachment, unknownCreator }: UpdateAttachment,
        { getState, dispatch },
    ) => {
        if (!attachment) {
            return
        }
        const state = getState() as GlobalState
        const { feedbackKeys } = state.fields

        try {
            await updateAttachmentValueDataApi(attachment.id, {
                ...newValue,
            })
        } catch (error) {
            let errorMessage = `${defaultUpdatingFieldErrorMessage}${field.title}.`
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof Error) {
                errorMessage = `${defaultUpdatingFieldErrorMessage}${field.title}. ${error.message}`
            }
            dispatch(addErrorNotification(errorMessage))
        } finally {
            await dispatch(fetchFields())
            handleFieldUpdateFeedback(feedbackKeys, field, dispatch)
            dispatch(
                addCreatorToAttachment({
                    field,
                    attachment: attachment,
                    unknownCreator: unknownCreator,
                }),
            )
        }
    },
)

export const removeAttachment = createAsyncThunk(
    "fields/inlineAttachment/removeAttachment",
    async ({ field, attachment }: RemoveAttachment, { dispatch }) => {
        if (!attachment) {
            return
        }
        try {
            await removeInlineAttachmentDataApi(
                field.itemId,
                field.fieldId,
                attachment.id,
            )
        } catch (error) {
            let errorMessage = `${defaultRemovingAttachmentErrorMessage}${field.title}.`
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof Error) {
                errorMessage = `${defaultUpdatingFieldErrorMessage}${field.title}. ${error.message}`
            }
            dispatch(addErrorNotification(errorMessage))
        } finally {
            await dispatch(fetchFields())
        }
    },
)

export const addAttachment = createAsyncThunk(
    "fields/inlineAttachment/addAttachment",
    async ({ files, field }: AttachmentParameters, { dispatch }) => {
        const promises: Promise<Api.Attachment>[] = []
        files.map((file) => {
            promises.push(
                addInlineAttachmentDataApi(field.itemId, field.fieldId, file),
            )
        })
        try {
            const settledPromises: PromiseSettledResult<Api.Attachment>[] =
                await Promise.allSettled(promises)
            settledPromises.map(async (result, index) => {
                if (result.status !== "fulfilled") {
                    const error = result.reason
                    const fileName = files[index].name
                    let errorMessage = `${defaultAddingAttachmentErrorMessage} file: ${fileName} in ${field.title}`
                    console.error(`${errorMessage} (${field.fieldId}): `, error)
                    if (error instanceof Error) {
                        errorMessage = `${defaultUpdatingFieldErrorMessage}${field.title}. ${error.message}`
                    }
                    dispatch(addErrorNotification(errorMessage))
                }
            })
        } catch (error) {
            let errorMessage = `${defaultAddingAttachmentErrorMessage}${field.title}.`
            console.error(`${errorMessage} (${field.fieldId}): `, error)
            if (error instanceof Error) {
                errorMessage = `${defaultUpdatingFieldErrorMessage}${field.title}. ${error.message}`
            }
            dispatch(addErrorNotification(errorMessage))
        } finally {
            await dispatch(fetchFields())
        }
    },
    {
        condition: (_, { getState }) => {
            const { fields } = getState() as GlobalState
            if (fields.itemId === -1 || fields.isFetchingFields) {
                // no react fields are present or
                // fetching is in progress
                return false
            }
        },
    },
)
